import * as _ from 'lodash';
import { action, observable, runInAction } from 'mobx';
import { DefaultBestPractice, IBestPractice } from 'src/Definitions';
import { BestPracticeDto, Client, IBestPracticeDto, SwaggerResponse } from 'src/generated_client';
import GlobalStore from 'src/stores/GlobalStore';
import DataTableStore from '../DataTableStore';
import NotificationStore from '../NotificationStore';

const ColumnSettingsDto = 'BestPracticeDto';

export default class BestPracticesStore {
    @observable
    public isLoading: boolean = false;
    @observable
    public isModalOpen: boolean;
    @observable
    public hasFileChanged: boolean = false;
    @observable
    public bestPractices: IBestPractice[];
    @observable
    public selectedBestPractice?: IBestPractice;
    public dataTableStore: DataTableStore<IBestPracticeDto, IBestPractice>;

    constructor(
        private globalStore: GlobalStore,
        private client: Client,
        private notificationStore: NotificationStore
    ) {
        this.dataTableStore = new DataTableStore<IBestPracticeDto, IBestPractice>(
            globalStore,
            ({ filter, page, orderBy, includeInactives }) =>
                client.getAllBestPractices(filter, page, undefined, orderBy, includeInactives),
            (bestPractice) => this.setupBestPractices(bestPractice),
            (userId, columns) =>
                client.apiColumnSettingsByUserIdByTypePut(userId, ColumnSettingsDto, columns)
        );
    }

    @action.bound
    public setHasFileChanged(hasFileChanged: boolean) {
        this.hasFileChanged = hasFileChanged;
    }

    @action.bound
    public closeDialog() {
        this.isModalOpen = false;
        this.selectedBestPractice = undefined;
    }

    @action.bound
    public disableBestPractice(bestPractice: IBestPractice) {
        this.isLoading = true;
        return this.client
            .archiveBestPractice(bestPractice.id)
            .then(() => this.dataTableStore.loadData())
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    @action.bound
    public deleteBestPractice(bestPractice: IBestPractice) {
        this.isLoading = true;
        const bestPracticeDto = new BestPracticeDto(bestPractice);
        return this.client
            .deleteBestPractice(bestPracticeDto)
            .then(() => {
                this.notificationStore.sendNotification(
                    '200',
                    bestPractice.name + ' has been deleted.'
                );
                this.dataTableStore.loadData();
            })
            .catch(() => {})
            .finally(() => {
                runInAction(() => {
                    this.isLoading = false;
                });
            });
    }

    @action.bound
    public getColumnSettingsAndBestPractices() {
        if (!this.globalStore.user) {
            return;
        }
        this.isLoading = true;
        return this.client
            .apiColumnSettingsByUserIdByTypeGet(this.globalStore.user!.id, ColumnSettingsDto)
            .then((data) => {
                runInAction(() => {
                    this.dataTableStore.setColumns(data.result);
                    const sortedColumn = _.find(this.dataTableStore.columns, (col) => !!col.sort);
                    if (sortedColumn) {
                        this.dataTableStore.setTableOrderBy(sortedColumn.sort);
                    }
                    this.dataTableStore.loadData();
                });
            })
            .catch(() => {})
            .finally(() => runInAction(() => (this.isLoading = false)));
    }

    @action.bound
    public getAllBestPractices(filter = '', pageNumber?: number, pageSize?: number) {
        if (!pageNumber) {
            pageNumber = 1;
        }
        if (!pageSize) {
            pageSize = 25;
        }
        this.isLoading = true;
        return this.client
            .getAllBestPractices(filter, pageNumber, pageSize)
            .then((resp: SwaggerResponse<IBestPracticeDto[]>) =>
                runInAction(() => {
                    this.bestPractices = this.setupBestPractices(resp.result);
                })
            );
    }

    @action.bound
    public openDialogWithBestPractice(bestPractice: IBestPractice) {
        this.isModalOpen = true;
        this.selectedBestPractice = bestPractice;
    }

    @action.bound
    public recoverBestPractice(bestPractice: IBestPractice) {
        this.isLoading = true;
        return this.client
            .reactivateBestPractice(bestPractice.id)
            .then(() => this.dataTableStore.loadData())
            .catch(() => {})
            .finally(() => {
                runInAction(() => {
                    this.isLoading = false;
                });
            });
    }

    private resetUIState(shouldClearFilter: boolean) {
        if (shouldClearFilter) {
            this.dataTableStore.clearFilter();
        }
        this.dataTableStore.loadData();
        this.closeDialog();
    }

    @action.bound
    public saveBestPractice(bestPractice: IBestPractice) {
        this.isLoading = true;
        let promise;
        const bestPracticeDto = new BestPracticeDto(bestPractice);

        if (bestPractice.isNew) {
            promise = this.client.createBestPractice(bestPracticeDto);
        } else {
            promise = this.client.updateBestPractice(bestPractice.id, bestPracticeDto);
        }

        return promise
            .then(() => {
                if (this.hasFileChanged) {
                    this.client
                        .uploadAttachmentFile(bestPracticeDto.attachmentFile, 'BestPractice')
                        .then(() => {
                            this.resetUIState(bestPractice.isNew);
                            this.notificationStore.sendNotification(
                                '200',
                                bestPractice.name + ' has been saved.'
                            );
                            this.isLoading = false;
                        })
                        .catch(() => {})
                        .finally(() => runInAction(() => {}));
                } else {
                    this.resetUIState(bestPractice.isNew);
                    this.isLoading = false;
                }
            })
            .catch(() => {})
            .finally(() => runInAction(() => {}));
    }

    @action.bound
    public downloadBestPracticeFile(url: string) {
        if (url === '') {
            this.notificationStore.sendNotification('404', 'No file exists to download.', {
                variant: 'error',
            });
            return;
        }
        const response = {
            file: url,
        };
        window.open(response.file);
        this.closeDialog();
    }

    private setupBestPractices = (bestPractices: IBestPracticeDto[]) => {
        return bestPractices.map((x) => this.setupBestPracticeMenuItems(this.addDefaultFields(x)));
    };

    private addDefaultFields = (bestPractice: IBestPracticeDto): IBestPractice => {
        return { ...DefaultBestPractice(), ...bestPractice };
    };

    private setupBestPracticeMenuItems = (bestPractice: IBestPractice) => {
        bestPractice.menuItems = [];
        bestPractice.menuItems.push({
            name: 'Edit',
            onClick: () => this.openDialogWithBestPractice(bestPractice),
        });
        bestPractice.menuItems.push({
            isConfirm: true,
            name: 'Delete',
            onClick: () => this.deleteBestPractice(bestPractice),
        });
        return bestPractice;
    };
}



// WEBPACK FOOTER //
// ./src/stores/BestPracticesStore/BestPracticesStore.ts